import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const EMPTY = {
  code: "", name: "", category: "Nails", duration_minutes: 60,
  price: 0, cost: 0, description: "", staff_ids: [], active: true, color: "#D8AAA2",
};

export default function ServiceFormDialog({ open, onOpenChange, initial, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [staff, setStaff] = useState([]);
  const isEdit = !!initial?.id;

  useEffect(() => {
    api.get("/staff").then((r) => setStaff(r.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    setForm(initial ? { ...EMPTY, ...initial, staff_ids: initial.staff_ids || [] } : EMPTY);
  }, [initial, open]);

  const set = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target?.value ?? e }));
  const toggleStaff = (id) => setForm((prev) => ({
    ...prev,
    staff_ids: prev.staff_ids.includes(id)
      ? prev.staff_ids.filter((item) => item !== id)
      : [...prev.staff_ids, id],
  }));

  const submit = async () => {
    if (!form.code || !form.name) return toast.error("Code and service name are required");
    const payload = {
      ...form,
      duration_minutes: Math.max(5, +form.duration_minutes || 60),
      price: +form.price || 0,
      cost: +form.cost || 0,
    };
    try {
      if (isEdit) {
        await api.put(`/services/${initial.id}`, { ...payload, id: initial.id });
        toast.success("Service updated");
      } else {
        await api.post("/services", payload);
        toast.success("Service created");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save service");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-3xl sm:max-w-[620px]">
        <DialogHeader>
          <DialogTitle className="font-display">{isEdit ? "Edit service" : "New service"}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 max-h-[72vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label className="text-[12px]">Service code *</Label>
              <Input data-testid="service-input-code" value={form.code} onChange={set("code")} placeholder="HAIR-001" className="rounded-xl font-mono" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-[12px]">Category</Label>
              <Select value={form.category || "Other"} onValueChange={set("category")}>
                <SelectTrigger data-testid="service-input-category" className="rounded-xl"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Nails", "Brows", "Lashes", "Aftercare", "Other"].map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label className="text-[12px]">Service name *</Label>
            <Input data-testid="service-input-name" value={form.name} onChange={set("name")} placeholder="Gel manicure" className="rounded-xl" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div className="grid gap-1.5">
              <Label className="text-[12px]">Duration (min)</Label>
              <Input data-testid="service-input-duration" type="number" min="5" step="5" value={form.duration_minutes} onChange={set("duration_minutes")} className="rounded-xl font-mono" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-[12px]">Price</Label>
              <Input data-testid="service-input-price" type="number" min="0" step="0.01" value={form.price} onChange={set("price")} className="rounded-xl font-mono" />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-[12px]">Service cost</Label>
              <Input data-testid="service-input-cost" type="number" min="0" step="0.01" value={form.cost} onChange={set("cost")} className="rounded-xl font-mono" />
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label className="text-[12px]">Description</Label>
            <Textarea data-testid="service-input-description" value={form.description || ""} onChange={set("description")} placeholder="What is included in this service?" className="rounded-xl resize-none" rows={3} />
          </div>
          <div className="grid gap-2">
            <Label className="text-[12px]">Team members who perform this service</Label>
            <div className="flex flex-wrap gap-2">
              {staff.map((member) => (
                <button
                  type="button"
                  key={member.id}
                  onClick={() => toggleStaff(member.id)}
                  className={`rounded-full px-3 py-1.5 text-[12px] border transition-colors ${
                    form.staff_ids.includes(member.id)
                      ? "bg-[#F8EEF4] border-[#E879A8]/50 text-[#9D3D6C] font-semibold"
                      : "bg-white border-black/10 text-[#6B6B73] hover:border-[#E879A8]/40"
                  }`}
                >
                  {member.name}
                </button>
              ))}
              {staff.length === 0 && <span className="text-[12px] text-[#8E8E93]">Add staff members first.</span>}
            </div>
          </div>
          <div className="flex items-center justify-between rounded-2xl bg-[#F5F5F7] border border-black/[0.05] px-3 py-2.5">
            <div>
              <div className="text-[13px] font-medium">Available for sale</div>
              <div className="text-[11px] text-[#8E8E93]">Inactive services stay in your records but cannot be selected on new sales.</div>
            </div>
            <button type="button" onClick={() => setForm((prev) => ({ ...prev, active: !prev.active }))} className={`w-11 h-6 rounded-full p-0.5 transition-colors ${form.active ? "bg-[#34C759]" : "bg-[#C7C7CC]"}`}>
              <span className={`block w-5 h-5 rounded-full bg-white shadow-sm transition-transform ${form.active ? "translate-x-5" : ""}`} />
            </button>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="service-save-btn" onClick={submit} className="rounded-full bg-black text-white hover:bg-black/85">
            {isEdit ? "Save changes" : "Create service"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}